/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import isNil from 'lodash/isNil'
import React from 'react'

import Animation from '@/components/animation/animation'
import AgendaBlock from '@/components/blocks-library/agenda/agenda-block'
import BenefitSectionBlock from '@/components/blocks-library/benefit-section/benefit-section'
import Content from '@/components/blocks-library/content/content'
import ContentImage from '@/components/blocks-library/content-image/content-image'
import { default as CtaComponent } from '@/components/blocks-library/cta/cta'
import Embed from '@/components/blocks-library/embed/embed'
import FeatureSection from '@/components/blocks-library/feature-section/feature-section'
import FeaturedContent from '@/components/blocks-library/featured-content/featured-content'
import FeaturedCta from '@/components/blocks-library/featured-cta/featured-cta'
import FeaturedExploreTopics from '@/components/blocks-library/featured-explore-topics/featured-explore-topics'
import FeaturedLeaders from '@/components/blocks-library/featured-leaders/featured-leaders'
import FeaturedMultipleContent from '@/components/blocks-library/featured-multiple-content/featured-multiple-content'
import FeaturedMultiplePaper from '@/components/blocks-library/featured-multiple-paper/featured-multiple-paper'
import FeaturedPaper from '@/components/blocks-library/featured-paper/featured-paper'
import FeaturedPersons from '@/components/blocks-library/featured-persons/featured-persons'
import { default as FeaturedStaticComponent } from '@/components/blocks-library/featured-static/featured-static'
import FormBlock from '@/components/blocks-library/form/form'
import GalleryWrapper from '@/components/blocks-library/gallery/gallery'
import Logos from '@/components/blocks-library/logos/logos'
import Section from '@/components/blocks-library/section/section'
import SeparatorSection from '@/components/blocks-library/separator-section/separator-section'
import ShowcaseSection from '@/components/blocks-library/showcase-section/showcase-section'
import { default as SliceComponent } from '@/components/blocks-library/slice/slice'
import { default as SliceNavigationComponent } from '@/components/blocks-library/slice-navigation/slice-navigation'
import Snippet from '@/components/blocks-library/snippet/snippet'
import UseCaseSection from '@/components/blocks-library/use-case-section/use-case-section'
import NavigableSection from '@/components/navigation/navigable-section'
import { anchorFormatted } from '@/utils/format'
import {
  getDatoCmsPctPageNavigationLabel,
  type NavigationItem
} from '@/utils/get-navigation-label'

//
type Props = {
  blocks: Queries.PctPageFragment['blocks'] | Queries.PctHomeFragment['blocks']
  animated?: boolean
  pageContext?: HomeCreatorPageContext | PageCreatorPageContext | null
  nodes: Highlights[]
}

const blockLibrary = {
  DatoCmsAgendaBlock: AgendaBlock,
  // DatoCmsAnswer: NoComponent,
  // DatoCmsBannerWithCta: NoComponent,
  // DatoCmsBasicSlide: NoComponent,
  DatoCmsBenefitSection: BenefitSectionBlock,
  // DatoCmsBenefitSectionItem: NoComponent,
  // DatoCmsBulletsSlide: NoComponent,
  DatoCmsCta: CtaComponent,
  // DatoCmsColumnCard: NoComponent,
  // DatoCmsColumnsSlide: NoComponent,
  DatoCmsContent: Content,
  DatoCmsContentImage: ContentImage,
  DatoCmsEmbed: Embed,
  DatoCmsFeaturedCta: FeaturedCta,
  DatoCmsFeatureSection: FeatureSection,
  // DatoCmsFeatureSectionItem: NoComponent,
  DatoCmsFeaturedContent: FeaturedContent,
  // DatoCmsFeaturedDialog: NoComponent,
  DatoCmsFeaturedExploreTopic: FeaturedExploreTopics,
  DatoCmsFeaturedLeader: FeaturedLeaders,
  DatoCmsFeaturedPaper: FeaturedPaper,
  DatoCmsFeaturedMultipleContent: FeaturedMultipleContent,
  DatoCmsFeaturedMultiplePaper: FeaturedMultiplePaper,
  DatoCmsFeaturedPerson: FeaturedPersons,
  // DatoCmsFeaturedPost: NoComponent,
  DatoCmsFeaturedStatic: FeaturedStaticComponent,
  DatoCmsForm: FormBlock,
  DatoCmsGallery: GalleryWrapper,
  // DatoCmsHero: NoComponent,
  // DatoCmsHighlight: NoComponent,
  // DatoCmsHighlightsSection: NoComponent,
  // DatoCmsImage: NoComponent,
  // DatoCmsLink: NoComponent,
  // DatoCmsLinkToContent: NoComponent,
  DatoCmsLogo: Logos,
  // DatoCmsMap: NoComponent,
  // DatoCmsNewsletterContent: NoComponent,
  // DatoCmsNewsletterSection: NoComponent,
  // DatoCmsPctChapter: NoComponent,
  // DatoCmsReport: NoComponent,
  DatoCmsSection: Section,
  DatoCmsSeparatorSection: SeparatorSection,
  DatoCmsShowcaseSection: ShowcaseSection,
  DatoCmsSlice: SliceComponent,
  // DatoCmsSliceItem: NoComponent,
  DatoCmsSliceNavigation: SliceNavigationComponent,
  // DatoCmsSliceNavigationItem: NoComponent,
  // DatoCmsSlide: NoComponent,
  // DatoCmsSlideColumn: NoComponent,
  // DatoCmsSlidesSection: NoComponent,
  DatoCmsSnippet: Snippet,
  // DatoCmsStatistic: NoComponent,
  // DatoCmsTable: NoComponent,
  // DatoCmsTableContent: NoComponent,
  // DatoCmsTranscription: NoComponent,
  DatoCmsUseCaseSection: UseCaseSection
  // DatoCmsUseCaseSectionItem: NoComponent
  // DatoCmsVideo: NoComponent,
  // DatoCmsVideoBanner: NoComponent,
  // DatoCmsVisionSection: NoComponent
}

const SectionBlocks: React.FC<Props> = ({
  blocks,
  animated,
  pageContext,
  nodes
}) => {
  // Find the first block with a navigation label
  const firstNavigableIndex =
    blocks?.findIndex((data) => {
      if (data === null || data === undefined) return false
      const navigationLabel = getDatoCmsPctPageNavigationLabel(
        data as NavigationItem | null
      )
      return !isNil(navigationLabel) && navigationLabel !== ''
    }) ?? -1

  // Find the actual first block (regardless of navigation label)
  const firstBlockIndex =
    blocks?.findIndex((data) => data !== null && data !== undefined) ?? -1

  /**
   * SIMPLIFIED CONTEXTUAL SPACING FOR BACKGROUND COMBINATIONS
   * Maintains visual consistency regardless of component order from CMS
   */

  /**
   * Determines whether a component typically has a background color/image.
   * For components with variants, checks the specific configuration.
   */
  const hasBackground = (typename: string, data?: unknown): boolean => {
    switch (typename) {
      // Components that typically have backgrounds
      case 'DatoCmsShowcaseSection': // Usually has dark/colored backgrounds
      case 'DatoCmsBenefitSection': // Often has colored backgrounds
      case 'DatoCmsFeaturedExploreTopic': // Has background colors
        return true

      // Featured Content - depends on display type
      case 'DatoCmsFeaturedContent':
        // When display !== 'basic', it uses HighlightFullWidth which has background
        return (data as { display?: string })?.display !== 'basic'

      // Featured Multiple Content - depends on display type
      case 'DatoCmsFeaturedMultipleContent':
        // Only 'highlighted_featured' variant has background (uses FeaturedHighlightFeatured)
        return (
          (data as { display?: string })?.display === 'highlighted_featured'
        )

      // Components that are typically white/transparent
      default:
        return false
    }
  }

  /**
   * Simplified contextual spacing - only handles background transitions
   */
  const getContextualSpacing = (
    currentType: string,
    prevType: string | null,
    isFirst: boolean,
    currentData?: unknown,
    prevData?: unknown
  ): string[] => {
    const modifiers: string[] = []

    // Skip contextual logic for first component
    if (isFirst || !prevType) {
      return modifiers
    }

    const currentHasBackground = hasBackground(currentType, currentData)
    const prevHasBackground = hasBackground(prevType, prevData)

    // Key transitions that need spacing adjustments
    if (prevHasBackground && !currentHasBackground) {
      modifiers.push('after-background') // More space after background component
    } else if (!prevHasBackground && currentHasBackground) {
      modifiers.push('before-background') // Less space before background component
    } else if (prevHasBackground && currentHasBackground) {
      modifiers.push('background-to-background') // Moderate space between backgrounds
    } else {
      modifiers.push('white-to-white') // Standard space between white components
    }

    return modifiers
  }

  /**
   * Helper function for special component CSS targeting
   */
  const getComponentTypeForCSS = (
    typename: string
  ): 'agenda' | 'logos' | undefined => {
    switch (typename) {
      case 'DatoCmsAgendaBlock':
        return 'agenda'
      case 'DatoCmsLogo':
        return 'logos'
      default:
        return undefined
    }
  }

  return (
    <>
      {blocks?.reduce((acc: React.ReactElement[], data, index) => {
        if (data === null || data === undefined) {
          return acc
        }
        const typeName = data.__typename
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const navigationLabel: string = getDatoCmsPctPageNavigationLabel(data)
        const Component = blockLibrary[typeName]

        const moreInfo = pageContext?.blocks
          ?.filter(
            (item) =>
              (item?.__typename === 'DatoCmsFeaturedMultipleContent' ||
                item?.__typename === 'DatoCmsFeaturedPaper') &&
              item.id === data.id
          )
          .pop()

        if (Component !== null) {
          const anchor =
            !isNil(navigationLabel) && navigationLabel !== ''
              ? anchorFormatted(navigationLabel)
              : `section-${index}`

          const isFirstNavigableSection = index === firstNavigableIndex
          const isFirstBlock = index === firstBlockIndex
          const componentTypeForCSS = getComponentTypeForCSS(typeName)

          // Get contextual spacing based on background combinations
          const prevBlock = index > 0 ? blocks[index - 1] : null
          const prevTypeName = prevBlock?.__typename || null
          const contextualModifiers = getContextualSpacing(
            typeName,
            prevTypeName,
            isFirstBlock, // Use isFirstBlock instead of isFirstNavigableSection for spacing
            data,
            prevBlock
          )

          const contextualClassName =
            contextualModifiers.length > 0
              ? contextualModifiers.join(' ')
              : undefined

          acc.push(
            <NavigableSection
              key={data.id}
              id={anchor}
              isFirst={isFirstBlock}
              componentTypeForCSS={componentTypeForCSS}
              className={contextualClassName}
            >
              <Animation disabled={!(animated ?? false)}>
                <Component {...data} moreInfo={moreInfo} nodes={nodes} />
              </Animation>
            </NavigableSection>
          )
        }
        return acc
      }, [])}
    </>
  )
}
export default SectionBlocks
